/**
 * Product slug helpers.
 * ---------------------
 * The original generator lowercased the whole title and replaced every run of
 * non-alphanumeric characters with a dash. That is fine for "Coloring Book
 * Edition 2" but it produced slugs like
 *
 *   mexico-city-travel-guide-2026-discover-inspiring-history-culture-and-cuisine
 *   -with-iconic-landmarks-like-the-palacio-de-bellas-artes-and-zocalo
 *
 * which is 142 characters. Long URLs are a real problem:
 *   - search engines truncate them in results, so the useful words get cut off
 *   - extra words past the first few add no ranking value and dilute the ones
 *     that do
 *   - they are awkward to share or paste into a message
 *
 * Strategy used here: keep the most meaningful leading words, cut at a word
 * boundary, and only add a short random suffix when the result is already taken.
 *
 * Alternatives considered and deliberately not used:
 *   - Pure ID slugs (/sales/p-7f3a91): short but throw away every keyword, and
 *     a keyword-free URL loses relevance that the title otherwise provides.
 *   - Full title untouched: the behaviour being fixed here.
 *   - Hash of the title: stable and collision-free, but turns a human readable
 *     URL into noise.
 *
 * IMPORTANT: this only applies to newly created or renamed products. Existing
 * slugs are left exactly as they are, because changing a slug breaks any link
 * that was already shared or indexed.
 */

/** Maximum length of the generated slug. Google shows roughly 50-60 URL
 *  characters before truncating, so 70 leaves useful headroom. */
export const MAX_SLUG_LENGTH = 70;

/** Never trim the slug below this, otherwise a very long first word would
 *  produce something useless like "supercalifragilistic". */
const MIN_SLUG_LENGTH = 24;

/**
 * Convert arbitrary text into a lowercase, dash-separated slug.
 * Accented characters are folded to ASCII so URLs stay clean.
 */
export const slugify = (value: string): string =>
  String(value || '')
    .toLowerCase()
    .normalize('NFKD')
    // Strip combining accents: "zócalo" -> "zocalo".
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

/**
 * Cut a slug down to `maxLength` without splitting a word in half.
 * Falls back to a hard cut when the first word alone is longer than the limit.
 */
export const truncateSlug = (slug: string, maxLength: number): string => {
  if (slug.length <= maxLength) return slug;

  const hardCut = slug.slice(0, maxLength);
  const lastDash = hardCut.lastIndexOf('-');
  const atWordBoundary =
    lastDash >= MIN_SLUG_LENGTH ? hardCut.slice(0, lastDash) : hardCut;

  return atWordBoundary.replace(/-+$/g, '');
};

/**
 * Build the slug for a product title, capped at a sensible length.
 * Example: "Mexico City Travel Guide 2026: Discover Inspiring History..."
 *       -> "mexico-city-travel-guide-2026-discover-inspiring-history"
 */
export const createProductSlug = (title: string): string =>
  truncateSlug(slugify(title), MAX_SLUG_LENGTH) || 'product';

/**
 * Short random suffix used only to break a slug tie, e.g. "a1b2c3".
 * 36^6 combinations makes an accidental repeat effectively impossible.
 */
export const randomSuffix = (): string =>
  Math.random().toString(36).slice(2, 8).padEnd(6, '0');

/**
 * Append a uniqueness suffix while respecting the maximum slug length, so
 * suffixing can never push a slug back over the limit.
 */
export const appendUniqueSuffix = (slug: string, suffix: string): string => {
  const trimmed = truncateSlug(slug, MAX_SLUG_LENGTH - suffix.length - 1);
  return `${trimmed || 'product'}-${suffix}`;
};
